/**
 * ExpenseForm.jsx — Create/edit variable expense form
 *
 * Features:
 * - Person name, category, description, amount, date inputs
 * - Job selector dropdown (optional — expenses can be unlinked)
 * - Category presets: Equipment, Travel, Meals, Supplies, Software, Insurance, Other
 * - Create or update mutation calls
 * - Validation with toast feedback
 */

import { useState, useEffect, useMemo } from 'react';
import { cn } from '../../lib/utils';
import { useCreateExpense, useUpdateExpense } from '../../hooks/useExpenses';
import { useJobs } from '../../hooks/useJobs';
import { useRoster } from '../../hooks/useRoster';
import { useAuth } from '../../hooks/useAuth';
import { useRBAC } from '../../hooks/useRBAC';
import { useSettings } from '../../hooks/useSettings';
import { useProjectsTree, buildTree, flattenTree } from '../../hooks/useProjects';
import { useToast } from '../layout/Toast';

// Exported so the bank review queue promotes into this same set — two lists
// would drift and split the P&L across near-duplicate category names.
export const CATEGORIES = ['Equipment', 'Travel', 'Meals', 'Supplies', 'Software', 'Insurance', 'Other'];
export const DEPARTMENTS = [
  { value: 'production', label: 'Production' },
  { value: 'post_production', label: 'Post-Production' },
  { value: 'originator', label: 'Originator' },
  { value: 'company', label: 'Company' },
];

export default function ExpenseForm({ initialExpense = null, onSave, onCancel }) {
  const { toast } = useToast();
  const { data: jobs = [] } = useJobs();
  const { data: roster = [] } = useRoster();
  const { profile } = useAuth();
  const { hasToolAccess } = useRBAC();
  const { data: settings } = useSettings();
  const createMutation = useCreateExpense();
  const updateMutation = useUpdateExpense();

  const projectsEnabled = hasToolAccess('projects', settings);
  const { data: projectsFlat = [] } = useProjectsTree();
  const projectOptions = useMemo(() => {
    if (!projectsEnabled) return [];
    return flattenTree(buildTree(projectsFlat));
  }, [projectsFlat, projectsEnabled]);

  // ── Build deduplicated person list: current user first, then roster ────
  const currentUserName = profile?.full_name || '';
  const personOptions = useMemo(() => {
    const names = new Set();
    names.add('Company');
    if (currentUserName) names.add(currentUserName);
    roster.forEach((r) => {
      const n = typeof r.name === 'string' ? r.name.trim() : '';
      if (n) names.add(n);
    });
    return [...names];
  }, [roster, currentUserName]);

  const [formData, setFormData] = useState({
    person: '',
    jobId: '',
    projectId: '',
    category: 'Equipment',
    department: 'production',
    description: '',
    amount: '',
    date: '',
  });

  // Pre-fill for edit mode, default person to current user
  useEffect(() => {
    if (initialExpense) {
      setFormData({
        person: initialExpense.person || currentUserName,
        jobId: initialExpense.jobId || '',
        projectId: initialExpense.projectId || '',
        category: initialExpense.category || 'Equipment',
        department: initialExpense.department || 'production',
        description: initialExpense.description || '',
        amount: initialExpense.amount || '',
        date: initialExpense.date || '',
      });
    } else {
      setFormData((prev) => ({ ...prev, person: prev.person || currentUserName }));
    }
  }, [initialExpense, currentUserName]);

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      // Smart department default when person changes
      if (field === 'person') {
        next.department = value.toLowerCase() === 'company' ? 'company' : prev.department === 'company' ? 'production' : prev.department;
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.person.trim()) {
      toast('Person name is required', 'error');
      return;
    }
    if (!formData.amount) {
      toast('Amount is required', 'error');
      return;
    }
    if (!formData.date) {
      toast('Date is required', 'error');
      return;
    }

    try {
      const expenseData = {
        person: formData.person.trim(),
        jobId: formData.jobId || null,
        projectId: formData.projectId || null,
        category: formData.category,
        department: formData.department,
        description: formData.description.trim() || null,
        amount: parseFloat(formData.amount),
        date: formData.date,
      };

      if (initialExpense?.id) {
        await updateMutation.mutateAsync({ ...expenseData, id: initialExpense.id });
        toast('Expense updated');
      } else {
        await createMutation.mutateAsync(expenseData);
        toast('Expense created');
      }

      onSave?.();
    } catch (err) {
      console.error(err);
      toast(`Failed to save: ${err.message}`, 'error');
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Person */}
        <div>
          <label className="block text-xs font-semibold text-text-dim mb-1.5">
            Person <span className="text-red-accent">*</span>
          </label>
          <select
            value={formData.person}
            onChange={(e) => handleChange('person', e.target.value)}
            className="w-full px-3 py-2 bg-surface border border-border rounded text-text-primary text-sm focus:outline-none focus:border-accent"
          >
            {personOptions.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        </div>

        {/* Date */}
        <div>
          <label className="block text-xs font-semibold text-text-dim mb-1.5">
            Date <span className="text-red-accent">*</span>
          </label>
          <input
            type="date"
            value={formData.date}
            onChange={(e) => handleChange('date', e.target.value)}
            className="w-full px-3 py-2 bg-surface border border-border rounded text-text-primary text-sm focus:outline-none focus:border-accent"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Category */}
        <div>
          <label className="block text-xs font-semibold text-text-dim mb-1.5">
            Category
          </label>
          <select
            value={formData.category}
            onChange={(e) => handleChange('category', e.target.value)}
            className="w-full px-3 py-2 bg-surface border border-border rounded text-text-primary text-sm focus:outline-none focus:border-accent"
          >
            {CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        {/* Department */}
        <div>
          <label className="block text-xs font-semibold text-text-dim mb-1.5">
            Department
          </label>
          <select
            value={formData.department}
            onChange={(e) => handleChange('department', e.target.value)}
            className="w-full px-3 py-2 bg-surface border border-border rounded text-text-primary text-sm focus:outline-none focus:border-accent"
          >
            {DEPARTMENTS.map((d) => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Amount */}
        <div>
          <label className="block text-xs font-semibold text-text-dim mb-1.5">
            Amount <span className="text-red-accent">*</span>
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-dim text-sm">$</span>
            <input
              type="number"
              value={formData.amount}
              onChange={(e) => handleChange('amount', e.target.value)}
              placeholder="0.00"
              min="0"
              step="0.01"
              className="w-full pl-7 pr-3 py-2 bg-surface border border-border rounded text-text-primary text-sm focus:outline-none focus:border-accent"
            />
          </div>
        </div>
      </div>

      {/* Job (optional) */}
      <div>
        <label className="block text-xs font-semibold text-text-dim mb-1.5">
          Job <span className="text-text-dim font-normal">(optional)</span>
        </label>
        <select
          value={formData.jobId}
          onChange={(e) => handleChange('jobId', e.target.value)}
          className="w-full px-3 py-2 bg-surface border border-border rounded text-text-primary text-sm focus:outline-none focus:border-accent"
        >
          <option value="">No linked job</option>
          {jobs.map((job) => (
            <option key={job.id} value={job.id}>
              {job.title || job.clientName || 'Untitled'} — {job.clientName}
            </option>
          ))}
        </select>
      </div>

      {/* Project (optional, super_admin only) */}
      {projectsEnabled && (
        <div>
          <label className="block text-xs font-semibold text-text-dim mb-1.5">
            Project <span className="text-text-dim font-normal">(optional)</span>
          </label>
          <select
            value={formData.projectId}
            onChange={(e) => handleChange('projectId', e.target.value)}
            className="w-full px-3 py-2 bg-surface border border-border rounded text-text-primary text-sm focus:outline-none focus:border-accent"
          >
            <option value="">— None —</option>
            {projectOptions.map((p) => (
              <option key={p.id} value={p.id}>
                {'— '.repeat(p.depth || 0)}{p.name} ({p.kind})
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Description */}
      <div>
        <label className="block text-xs font-semibold text-text-dim mb-1.5">
          Description
        </label>
        <input
          type="text"
          value={formData.description}
          onChange={(e) => handleChange('description', e.target.value)}
          placeholder="Brief description of the expense"
          className="w-full px-3 py-2 bg-surface border border-border rounded text-text-primary placeholder-text-dim text-sm focus:outline-none focus:border-accent"
        />
      </div>

      {/* Buttons */}
      <div className="flex justify-end gap-2 pt-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm text-text-dim hover:text-text-primary transition-colors"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 bg-accent text-bg text-sm font-semibold rounded hover:bg-accent-hover transition-colors disabled:opacity-50"
        >
          {isLoading ? 'Saving...' : initialExpense?.id ? 'Update Expense' : 'Add Expense'}
        </button>
      </div>
    </form>
  );
}
